import { useState } from 'react';
import { post } from '../../../Services/ApiClient';
import { useEffectAsync } from '../../../helpers';
import {
	WizardAction,
	WizardFinalStep,
	WizardRootItem,
} from '../Interfaces/Wizard.interface';
import { isArray } from 'lodash';
import useApiClient from '../../../Hooks/useApiClient';
import useLoading from '../../Base/Hook/useLoading';
import useNotification from '../../../Hooks/useNotification';
import { uploadWizardFiles } from '../Helpers/WizardFiles.helper';

export default function useWizard({
	key,
	initialData,
	onWizardDone,
}: {
	key?: string;
	initialData?: object;
	onWizardDone: (action: WizardAction) => void;
}) {
	const [activeStep, setActiveStep] = useState<WizardRootItem | null>(null);
	const { displayError } = useApiClient();
	const { showNotificationAlert } = useNotification();
	const { isLoading, setIsLoading } = useLoading();

	const wizardPost = async (values?: object) => {
		if (!key) return;

		try {
			const data = await uploadWizardFiles(values ?? {});
			const result = await post(`/wizard/${key}/tree`, data);
			return isArray(result.data)
				? (result.data as WizardRootItem[])
				: (result.data as WizardFinalStep);
		} catch (e) {
			displayError(e);
		}
	};

	const isWizardFinalStep = (v: unknown): v is WizardFinalStep => {
		return (
			!!v && typeof v === 'object' && 'key' in (v as Record<string, unknown>)
		);
	};

	const submitStep = async (values: object) => {
		setIsLoading(true);
		const wizard = await wizardPost({ ...values, step: activeStep?.key });
		if (!wizard) {
			setIsLoading(false);
			return;
		}

		if (isArray(wizard) && wizard.length > 0) {
			const step = wizard[0];

			if (step.errors && (step.errors?.length || 0) > 0) {
				step.errors.forEach(error => {
					showNotificationAlert({ content: error.message });
				});
			}

			if (step.key === 'done' && step.settings?.action) {
				onWizardDone(step.settings.action);
			} else {
				setActiveStep(wizard[0]);
				setIsLoading(false);
			}
			return;
		}

		if (isWizardFinalStep(wizard)) {
			if (wizard.key === 'done') {
				onWizardDone(wizard.settings.action);
			} else {
				setIsLoading(false);
			}
		}
	};

	useEffectAsync(async () => {
		await submitStep(initialData || {});
	}, [key]);

	return {
		activeStep,
		submitStep,
		isLoading,
	};
}
